import { ATTRACTION_LADDER, purchaseCost } from '../config/catalog';
import { entityDef, expansionCost } from '../config/balancing';
import { deriveParkGoal } from '../sim/derivedGoal';
import { computeParkScore } from '../sim/economy';
import { hasKind } from '../sim/unlocks';
import { parcelById } from '../config/map';
import { hasMacroEntitlement, plotById, plotLabel, remainingMacroEntitlementCost } from '../spatial/plots';
import type { OnboardingStep } from '../onboarding/onboarding';
import type { EntityKind, ParkState } from '../types';
import { getLocale, localeShort } from './locale';
import { STRINGS } from './strings';

export {
  LOCALES,
  LOCALE_STORAGE_KEY,
  cycleLocale,
  detectLocale,
  getLocale,
  initLocale,
  localeShort,
  onLocaleChange,
  setLocale,
  type LocaleId,
} from './locale';

export function t(key: string, vars?: Record<string, string | number>): string {
  const table = STRINGS[getLocale()] ?? STRINGS.en;
  let text = table[key] ?? STRINGS.en[key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.replaceAll(`{${name}}`, String(value));
    }
  }
  return text;
}

export function entityName(kind: EntityKind): string {
  return t(`entity.${kind}`);
}

export function parcelName(parcelId: string): string {
  const key = `parcel.${parcelId}`;
  const localized = t(key);
  if (localized !== key) {
    return localized;
  }
  if (plotById(parcelId)) {
    return plotLabel(parcelId);
  }
  return parcelById(parcelId)?.label ?? parcelId;
}

export function paletteLabel(id: string, fallback: string): string {
  const key = `palette.${id}`;
  const localized = t(key);
  return localized === key ? fallback : localized;
}

export function formatNextGoal(state: ParkState): string {
  for (const kind of ATTRACTION_LADDER) {
    if (hasKind(state, kind)) {
      continue;
    }
    const spec = entityDef(kind).unlock;
    const label = t('goal.next', { name: entityName(kind) });
    if (spec.requiresKind && !hasKind(state, spec.requiresKind)) {
      return `${label} · ${t('goal.need', { name: entityName(spec.requiresKind) })}`;
    }
    if (spec.requiresParcel && !hasMacroEntitlement(state.unlockedLandIds, spec.requiresParcel)) {
      const remaining = remainingMacroEntitlementCost(state.unlockedLandIds, spec.requiresParcel);
      const cost = remaining > 0 ? remaining : expansionCost(spec.requiresParcel);
      return `${label} · ${t('goal.unlock', {
        name: parcelName(spec.requiresParcel),
        have: state.coins,
        cost,
      })}`;
    }
    if (spec.minParkScore !== undefined) {
      const score = computeParkScore(state);
      if (score < spec.minParkScore) {
        return `${label} · ${t('goal.parkScore', { have: score, need: spec.minParkScore })}`;
      }
    }
    const cost = entityDef(kind).purchaseCost;
    if (state.coins < cost) {
      return `${label} · ${t('goal.coins', { have: state.coins, cost })}`;
    }
    return `${label} · ${t('goal.ready', { cost })}`;
  }
  return `${t('goal.coasterBuilt')} · ${t('goal.keepGrowing')}`;
}

/** One short line for the derived post-tutorial goal. Prices come from live state. */
export function formatDerivedGoal(state: ParkState): string {
  const goal = deriveParkGoal(state);
  const name = entityName(goal.kind);
  if (goal.id === 'starter_upgrade') {
    return t('goal.upgrade', { name, have: state.coins, cost: goal.price });
  }
  if (goal.id === 'ferris') {
    return t('goal.ferris', { name, cost: goal.price, gap: goal.gap });
  }
  return t('goal.action', { name, have: state.coins, cost: goal.price });
}

/** First-session goal text. Guided steps keep their short lines. After Drink, the chip is derived. */
export function formatHudGoal(state: ParkState, step?: OnboardingStep): string {
  if (step === 'build_ride') {
    return t('guide.buildRide');
  }
  if (step === 'build_snack') {
    return t('guide.buildSnack');
  }
  if (step === 'collect_drink') {
    return t('guide.collectDrink', { have: state.coins, cost: purchaseCost('drink_kiosk') });
  }
  if (step === 'build_drink') {
    return t('guide.buildDrink');
  }
  if (step === 'complete') {
    return formatDerivedGoal(state);
  }
  return formatNextGoal(state);
}

export function languageButtonLabel(): string {
  return `${t('hud.language')} ${localeShort()}`;
}
